/**
 * Model for topic attributes
 */
define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
  "modules/header/kbMenu/kbMenuModel",
], function (Backbone, $, _, Messenger, Properties, SortableListModel, KbMenuModel) {
  "use strict";

  const TopicAttributeModel = {};

  TopicAttributeModel.topicAttribute = Backbone.Model.extend({
    backup: function () {
      this.onSynced(function () {
        this.wasDirty = this.dirty;
        if (!this.dirty) {
          this.cloneCopy = this.clone();
        }
      }, this);
    },
    parse: function (response) {
      if (!response) {
        return response;
      }
      // Flatten the localized values to a series of "localizedAttributeValue_LANGUAGE_ID" values
      const localizedAttributeBindings = response.localizedAttributeBindings;
      if (!localizedAttributeBindings || localizedAttributeBindings.length === 0) {
        return response;
      }
      for (let i = 0; i < localizedAttributeBindings.length; ++i) {
        const localizedAttributeValue = localizedAttributeBindings[i];
        response["localizedAttributeValue_" + localizedAttributeValue.languageId] =
          localizedAttributeValue.attributeValue;
      }
      return response;
    },

    toJSON: function (options) {
      if (options && (options.parse || options.saving)) {
        // Take the localized attribute values that were added in parse() and produce
        // child localizedAttributeBinding objects.
        const json = {};
        const attributes = this.attributes;
        const localizedValueRe = /localizedAttributeValue_/i;
        let localizedAttributeBindings = attributes.localizedAttributeBindings;
        const localizedValueMap = {};
        if (localizedAttributeBindings) {
          for (let i = 0; i < localizedAttributeBindings.length; ++i) {
            localizedValueMap[localizedAttributeBindings[i].languageId] =
              localizedAttributeBindings[i];
          }
        }
        localizedAttributeBindings = [];
        for (let prop in attributes) {
          if (attributes.hasOwnProperty(prop) && prop !== "localizedAttributeBindings") {
            const value = attributes[prop];
            if (localizedValueRe.test(prop)) {
              if (!_.isUndefined(value) && value !== null) {
                const languageId = parseInt(prop.substring(24), 10);
                let localizedAttributeValue = localizedValueMap[languageId];
                if (localizedAttributeValue) {
                  localizedAttributeValue.attributeValue = value;
                } else {
                  localizedAttributeValue = {
                    bindingId: attributes.id,
                    languageId: languageId,
                    attributeValue: value,
                  };
                }
                localizedAttributeBindings.push(localizedAttributeValue);
              }
            } else {
              json[prop] = value;
            }
          }
        }
        json.localizedAttributeBindings = localizedAttributeBindings;
        return json;
      }
      return this.constructor.__super__.toJSON.apply(this, arguments);
    },

    restore: function () {
      this.set(this.cloneCopy.attributes);
      this.dirty = this.wasDirty;
    },

    validateAttribute: function (attribute, value, allAttributes) {
      // Attribute values no longer have a maximum length
      if (attribute === "attributeId") {
        // value must be unique
        for (let i = 0; i < this.collection.length; i++) {
          const attr = this.collection.at(i);
          if (this !== attr) {
            if (attr.get("attributeId") == value) {
              return $.t("topic.attributes.errorDuplicateAttributeId", {
                attributeName: attr.get("attributeName"),
              });
            }
          }
        }
      }

      if (attribute === "attributeValue") {
        // Validate the attribute value to make sure it is valid for given data type
        const kbAttributes = this.attributeList;
        let dataType = "";
        const thisAttributeId = allAttributes ? allAttributes.attributeId : this.get("attributeId");

        kbAttributes?.forEach((model) => {
          if (model.id === thisAttributeId) {
            dataType = model.get("dataType");
          }
        });

        const validConditionsArray = ["yes", "no", "true", "false", "1", "0"];

        switch (dataType) {
          case KbMenuModel.ATTRIBUTE_DATA_TYPE.NUMBER:
            // Convert the value from the user to number
            if (isNaN(Number(value))) {
              return $.t("topic.attributes.attributeInvalidNumberError");
            }
            break;
          case KbMenuModel.ATTRIBUTE_DATA_TYPE.BOOLEAN:
            // Valid values are yes/no, true/false and 1/0
            if (validConditionsArray.indexOf(value.toLowerCase()) === -1) {
              return $.t("topic.attributes.attributeInvalidBooleanError");
            }
        }
      }
    },
  });

  TopicAttributeModel.topicAttributes = Backbone.Collection.extend({
    model: TopicAttributeModel.topicAttribute,

    addAttribute: function (respId, attId, attValue, attName) {
      const newAttribute = new TopicAttributeModel.topicAttribute({
        responseId: respId,
        attributeId: attId,
        attributeValue: attValue,
        attributeName: attName,
        localizedAttributeBindings: [],
      });
      this.add(newAttribute, { at: 0 });
      return newAttribute;
    },

    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "attributeName");
    },

    parse: function (models, options) {
      if (!models || !models.length) {
        return models;
      }
      const result = [];
      options = _.extend({ parse: true }, options);
      for (let i = 0; i < models.length; ++i) {
        result.push(new TopicAttributeModel.topicAttribute(models[i], options));
      }
      return result;
    },

    _buildPathToResponseMap: function (map, path, conversationNode) {
      const currentPath = path + conversationNode.get("description");
      map[currentPath] = conversationNode.get("responseId");
      conversationNode.decisionCol.each(function (node) {
        this._buildPathToResponseMap(map, currentPath + "  ", node);
      }, this);
    },
  });

  Messenger.reqres.setHandler(
    "fetch:topic:response:attributes:byResponseIdAndAttributeId",
    function (responseId, attributeId) {
      const kbId = Messenger.reqres.request("active:kb");
      const topicAttribute = new TopicAttributeModel.topicAttribute();
      topicAttribute.url =
        Properties.adminRootURL +
        "/kbs/" +
        kbId +
        "/intents/responses/" +
        responseId +
        "/attribute/" +
        attributeId;
      topicAttribute.fetch();
      return topicAttribute;
    },
  );

  Messenger.reqres.setHandler("fetch:topic:response:attributes:headers", function () {
    const Header = SortableListModel.headerModel;
    // noinspection HtmlUnknownAttribute
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.attributes.name"),
        modelAttribute: "attributeName",
        selectable: true,
        sortable: true,
        filterable: true,
        longTextField: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "attributeValue",
        width: "15px",
        customHtml: '<span class="textbox-placeholder">ATTRIBUTE VALUE</span>',
      }),
      new Header({
        headerName: "",
        modelAttribute: "attributeName",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteAttribute pull-right' +
          ' data-i18n="[title]admin.attributes.deleteAttribute"><i class="icon-remove"></i></button>',
      }),
    ]);
  });

  return TopicAttributeModel;
});
